<!--
/*
 * Компонент TransactionList — переиспользуемый список транзакций с фильтрацией
 *
 * Основные функции:
 * - Фильтрация по статусам оплаты (дефолт — "Завершена")
 * - Фильтрация по временным статусам (Текущая/Будущая/Прошлая)
 * - Кнопка отмены только на последней активной транзакции
 * - Диалог подтверждения отмены
 */
-->
<script setup lang="ts">
// Реактивность Vue
import { computed, ref, watch } from 'vue'
// Стор администрирования — отмена транзакции
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Утилита Pinia для реактивных ссылок на стейт стора
import { storeToRefs } from 'pinia'
// Перечисление статусов оплаты из сгенерированных proto-стабов
import { PaymentStatus } from '@vpn-tg/vue-client-grpc/proto/enums/transactionStatus'
// Хелперы форматирования транзакций
import {
  statusOptions,
  timeStatusOptions,
  getTimeStatus,
} from '@/modules/admin/utils/transactionHelpers'
import type { TimeStatusValue } from '@/modules/admin/utils/transactionHelpers'
// Компонент карточки транзакции
import TransactionCard from '@/modules/admin/components/TransactionCard.vue'

// Импорт gRPC клиента для локального поиска пользователей
import { $grpcClient } from '@/grpc-client/grpc-client'
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
import { grpcReq } from '@/grpc-client/grpc/grpc-request'

// Интерфейс пропсов
interface ITransactionListProps {
  /** Скрыть User ID в карточках (при использовании внутри карточки пользователя) */
  hideUserID?: boolean
}

// Пропсы с дефолтами
withDefaults(defineProps<ITransactionListProps>(), {
  hideUserID: false,
})

// Событие: транзакция отменена или изменились фильтры статусов — родитель перезагружает данные
const emit = defineEmits<{
  cancelled: []
  statusChanged: [statuses: PaymentStatus[]]
}>()

// Стор администрирования
const adminStore = useAdminStore()
// Реактивная ссылка на список транзакций из стора
const { transactions } = storeToRefs(adminStore)

// Выбранные статусы оплаты (дефолт — "Завершена")
const filterStatuses = ref<PaymentStatus[]>([PaymentStatus.PaymentCompleted])
// Выбранные временные статусы
const filterTimeStatuses = ref<TimeStatusValue[]>([])

// Транзакции после фронтовой фильтрации по временным статусам
const filteredTransactions = computed(() => {
  if (filterTimeStatuses.value.length === 0) return transactions.value

  return transactions.value.filter((tx) => {
    const ts = getTimeStatus(tx)
    return ts.value && filterTimeStatuses.value.includes(ts.value)
  })
})

// ID последней (самой новой) активной транзакции — только у неё показывается кнопка "Отменить"
const lastCompletedTxId = computed(() => {
  const found = filteredTransactions.value.find(
    (tx) => tx.paymentStatus === PaymentStatus.PaymentCompleted,
  )
  return found?.id ?? null
})

// Идентификатор транзакции, выбранной для отмены (null если диалог закрыт)
const cancelTransactionId = ref<string | null>(null)
// Флаг открытия диалога подтверждения отмены транзакции
const isCancelDialogOpen = ref(false)

// Состояние диалога переноса транзакции
const isMoveDialogOpen = ref(false)
const moveTransactionId = ref<string | null>(null)
const targetUserID = ref('')
const moveError = ref('')

// Локальное состояние для поиска пользователей в диалоге переноса
const searchQuery = ref('')
const searchedUsers = ref<any[]>([])
const isSearchingUsers = ref(false)
let searchTimeout: ReturnType<typeof setTimeout>

watch(searchQuery, (newVal) => {
  clearTimeout(searchTimeout)
  const query = newVal?.trim() ?? ''

  // Если пользователь уже выбран (его displayName совпадает с query), не запускаем поиск
  const isAlreadySelected = searchedUsers.value.some(
    (u) => u.id === targetUserID.value && u.displayName === query,
  )
  if (isAlreadySelected) {
    isSearchingUsers.value = false
    return
  }

  if (query.length < 2) {
    searchedUsers.value = []
    isSearchingUsers.value = false
    return
  }

  isSearchingUsers.value = true
  searchTimeout = setTimeout(async () => {
    try {
      const [ok, response] = await grpcCall(
        (_args, options) =>
          $grpcClient.UserEndpoint.getUsers(
            grpcReq({
              filters: { ids: [], searchString: query },
              pagination: { page: 1, size: 20 },
              sorts: [],
            }),
            options,
          ),
        'searchUsersLocal',
      )
      if (ok && response && !response.error) {
        searchedUsers.value = (response.users || []).map((u) => ({
          id: u.id,
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          telegramID: u.telegramID || '',
          displayName: `${u.firstName || ''} (${u.email || u.telegramID || u.id})`.trim(),
        }))
      } else {
        searchedUsers.value = []
      }
    } catch (e) {
      searchedUsers.value = []
    } finally {
      isSearchingUsers.value = false
    }
  }, 400)
})

const openMoveDialog = (transactionId: string) => {
  moveTransactionId.value = transactionId
  targetUserID.value = ''
  searchQuery.value = ''
  searchedUsers.value = []
  isSearchingUsers.value = false
  moveError.value = ''
  isMoveDialogOpen.value = true
}

const confirmMoveTransaction = async () => {
  if (!moveTransactionId.value) return
  if (!targetUserID.value.trim()) {
    moveError.value = 'Выберите пользователя для переноса'
    return
  }

  const success = await adminStore.moveTransaction(moveTransactionId.value, targetUserID.value.trim())
  if (success) {
    moveTransactionId.value = null
    targetUserID.value = ''
    isMoveDialogOpen.value = false
    emit('cancelled')
  }
}

/**
 * Открывает диалог подтверждения отмены транзакции
 *
 * @param {string} transactionId - Идентификатор транзакции для отмены
 */
const openCancelDialog = (transactionId: string) => {
  cancelTransactionId.value = transactionId
  isCancelDialogOpen.value = true
}

/**
 * Подтверждает отмену транзакции и уведомляет родителя
 *
 * Основные действия:
 * - Вызывает adminStore.cancelTransaction
 * - При успехе закрывает диалог и эмитит cancelled
 */
const confirmCancelTransaction = async () => {
  if (!cancelTransactionId.value) return

  const success = await adminStore.cancelTransaction(cancelTransactionId.value)
  if (success) {
    cancelTransactionId.value = null
    isCancelDialogOpen.value = false
    emit('cancelled')
  }
}

// При изменении статусов оплаты — уведомляем родителя для перезагрузки данных с бэкенда
watch(filterStatuses, (statuses) => {
  emit('statusChanged', statuses)
})
</script>

<template>
  <div class="d-flex flex-column ga-2">
    <!-- Фильтр по статусам оплаты (дефолт — "Завершена") -->
    <v-select
      v-model="filterStatuses"
      :items="statusOptions"
      item-title="title"
      item-value="value"
      label="Статусы"
      variant="outlined"
      density="compact"
      hide-details
      multiple
      clearable
      chips
    />

    <!-- Фильтр по временным статусам (Текущая/Будущая/Прошлая) -->
    <v-select
      v-model="filterTimeStatuses"
      :items="timeStatusOptions"
      item-title="title"
      item-value="value"
      label="Период"
      variant="outlined"
      density="compact"
      hide-details
      multiple
      clearable
      chips
    />

    <!-- Список транзакций -->
    <div
      v-if="filteredTransactions.length === 0"
      class="text-center text-text-disabled pa-4"
    >
      Транзакции не найдены
    </div>

    <transaction-card
      v-for="tx in filteredTransactions"
      :key="tx.id"
      :transaction="tx"
      :hide-user-i-d="hideUserID"
      :show-cancel="tx.id === lastCompletedTxId"
      @cancel="openCancelDialog"
      @move="openMoveDialog"
    />

    <!-- Диалог подтверждения отмены транзакции -->
    <v-dialog v-model="isCancelDialogOpen" max-width="420">
      <v-card class="d-flex flex-column ga-4 pa-4">
        <div class="text-h6">Отмена транзакции</div>

        <div class="text-body-1">
          Вы уверены, что хотите отменить эту транзакцию? Дни транзакции будут убраны из подписки пользователя.
        </div>

        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="isCancelDialogOpen = false"> Отмена </v-btn>

          <v-btn color="error" variant="flat" @click="confirmCancelTransaction"> Подтвердить </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Диалог переноса транзакции -->
    <v-dialog v-model="isMoveDialogOpen" max-width="420">
      <v-card class="d-flex flex-column ga-4 pa-4">
        <div class="text-h6">Перенос транзакции</div>

        <div class="text-body-1">
          Выберите пользователя, которому вы хотите передать эту транзакцию.
        </div>

        <v-autocomplete
          v-model="targetUserID"
          v-model:search="searchQuery"
          :items="searchedUsers"
          item-title="displayName"
          item-value="id"
          label="Поиск пользователя"
          variant="outlined"
          density="compact"
          :error-messages="moveError"
          hide-details="auto"
          :loading="isSearchingUsers"
          no-filter
          hide-no-data
          clearable
          placeholder="Введите имя, email, ID..."
        />

        <div class="d-flex flex-column ga-2 w-100">
          <v-btn color="primary" variant="flat" block @click="confirmMoveTransaction"> Перенести </v-btn>
          <v-btn variant="text" block @click="isMoveDialogOpen = false"> Отмена </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
